import { TouchableOpacity } from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import {
  CommonActions,
  getFocusedRouteNameFromRoute,
  useNavigation,
} from "@react-navigation/native";
import { useTheme } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import { KeysMatching } from "../../../styled";
import BulkListHeaderButton from "@scenes/bulk-request/BulkRequestHeaderButton";
import LanguageSelection from "@navigations/HeaderRightButtons/LanguageSelection";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { useDispatch, useStore } from "react-redux";
import { setBuilderProduct, setProductType } from "@redux/slices/appStateSlice";
import { setSectionName } from "@redux/slices/searchFilters";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { getSectionName } from "@utils/product/searchResultsNavigation";
import {
  isSettingSearchRoute,
  refreshSavedSearchForEntry,
} from "@scenes/jewelry-builder/utils/utils";

type LeftHeaderProps = {
  textColor?: KeysMatching<DefaultTheme, string>;
  canGoBack?: boolean;
};

const headerRightMobileJson = {
  cart: {
    name: "cart",
    acceptedRoutes: [
      Routes.Explore,
      Routes.RequestHistory,
      Routes.RequestHistoryDetail,
      Routes.More,
      Routes.HomeScreen,
      Routes.PagesScreen,
      Routes.ChangePriceMode,
      Routes.Logout,
      Routes.SearchResultScreen,
      Routes.FilterScreen,
      Routes.FilterSearchScreen,
      Routes.DefaultSearchGroup,
      Routes.ProductSearchGroupScreen,
      Routes.DeleteAccount,
      Routes.Languages,
      Routes.SearchText,
      Routes.TermsOfUse,
      Routes.BiometricAuthorization,
      Routes.Search,
      Routes.ChooseAcategory,
      Routes.ChooseARequestScreen,
      Routes.Currency,
      Routes.FavoritesListScreen,
      Routes.RequestDetailScreen,
      Routes.RequestModifyScreen,
      Routes.PDPScreen,
      Routes.FindMatchScreen,
      Routes.SelectCollectionScreen,
      Routes.SearchGroupNavigator,
      Routes.PostOnBoardingScreen,
      Routes.FilterSplitScreen,
      Routes.TermsOfUseScreen,
      Routes.VerifiedScreen,
      Routes.SavedSearch,
      Routes.Favorites,
      Routes.ReviewPage,
    ],
  },
};

const GetHeaderRight = ({ color, focusedRouteName }: any) => {
  return (
    <HeaderRightContainer style={{ paddingHorizontal: 16 }}>
      {Object.values(headerRightMobileJson).map((val, index) => {
        if (val.acceptedRoutes.includes(focusedRouteName)) {
          switch (val.name) {
            case "cart":
              return <BulkListHeaderButton key={index} color={color} />;
            case "language_selection":
              return <LanguageSelection key={index} color={color} />;
            default:
              return null;
          }
        }
      })}
    </HeaderRightContainer>
  );
};

const GetHeaderLeft = ({ textColor, canGoBack = false }: LeftHeaderProps) => {
  const navigation = useNavigation();
  const theme = useTheme();
  const dispatch = useDispatch();
  // Read filters from the store on press instead of subscribing: this header
  // renders on every screen and `auxPayload` changes on each filter keystroke.
  const store = useStore<any>();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const mRoutes = navigation?.getState()?.routes;

  const restoreSettingSearchOnBack = () => {
    if (!enableJewelryBuilder) return;

    const navState = navigation.getState?.();
    if (!navState || navState.index < 1) return;

    const previousRoute = navState.routes[navState.index - 1];
    const previousParams = previousRoute?.params as any;
    if (!isSettingSearchRoute(previousRoute?.name, previousParams)) {
      return;
    }

    const updated = refreshSavedSearchForEntry(
      { name: previousRoute.name, params: previousParams },
      store.getState()?.searchFilters?.auxPayload
    );
    const productType = previousParams?.productType;
    const sectionName =
      previousParams?.sectionName || getSectionName(productType, "");

    dispatch(setSectionName(sectionName));
    dispatch(setProductType(productType));
    dispatch(setBuilderProduct(productType));

    if (
      updated.params?.saved_search &&
      updated.params.saved_search !== previousParams?.saved_search
    ) {
      navigation.dispatch({
        ...CommonActions.setParams({
          saved_search: updated.params.saved_search,
        }),
        source: previousRoute.key,
      });
    }
  };

  const handleBackNavigation = () => {
    let fromHomePage = false;

    if (mRoutes && mRoutes.length > 0) {
      fromHomePage = mRoutes[mRoutes.length - 1]?.params?.fromHomePage;
    }

    if (fromHomePage) {
      // Pop this screen off Explore before leaving the tab. Otherwise the
      // product page stays on Explore's stack and the next Explore tap
      // restores it.
      if (navigation.canGoBack()) {
        navigation.goBack();
      }
      navigate(Routes.HomeNavigator, {
        screen: Routes.HomeScreen,
      });
    } else {
      restoreSettingSearchOnBack();
      navigation.goBack();
    }
  };

  return canGoBack ||
    (navigation?.getState()?.routes?.length > 1 && navigation?.canGoBack()) ? (
    <TouchableOpacity
      onPress={handleBackNavigation}
      style={{ paddingLeft: 16, paddingRight: 8 }}
    >
      <VDBIcon
        variant={IconsVariant.VDBCustom}
        name="left"
        size={24}
        color={textColor && textColor.length > 0 ? theme[textColor] : "black"}
      />
    </TouchableOpacity>
  ) : (
    ""
  );
};

const getFocusedRouteName = (route: any) => {
  if (!route?.state) {
    return getFocusedRouteNameFromRoute(route);
  }
  const routeState = route.state;
  const nestedRoute = routeState.routes[routeState.index];
  return getFocusedRouteName(nestedRoute);
};

const HeaderRightContainer = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  align-items: center;
`;

export { GetHeaderRight, GetHeaderLeft, getFocusedRouteName };
